/**
 * App update flow (docs/2026-10-18_PWA_update_flow.md).
 *
 * A deploy publishes a new service worker. It installs and precaches the new build in the
 * background, then WAITS (service-worker.js no longer calls skipWaiting on its own). This
 * module decides when the waiting worker may take over:
 *
 *   - silently, the moment it is safe: nothing in flight (no API write, no chat stream, no
 *     permission prompt), no unsent chat draft, not on an edit screen. Checked on launch,
 *     on every return to the foreground, and every 6 hours while the app stays open;
 *   - via the 「更新」 bar (UpdateBar.jsx) only when a tab has been visibly active for over
 *     an hour without a safe moment.
 *
 * Taking over = postMessage SKIP_WAITING → the worker activates → `controllerchange` →
 * one reload. `controllerchange` also fires on the very first install (clientsClaim), so
 * the reload is gated on `askedToSkip`: we only reload when we asked.
 *
 * Filters live in the URL and the KPI snapshot in sessionStorage, so the reload lands on
 * the same screen with the numbers already painted.
 */

const CHECK_EVERY_MS = 6 * 60 * 60 * 1000;
const OVERDUE_AFTER_MS = 60 * 60 * 1000; // visible time with a waiting worker before the bar shows
const TICK_MS = 60 * 1000;
/** Screens with unsaved form state until the user taps save. */
const HOLD_PATHS = ['/settings/goaledit', '/settings/actionedit'];

let waiting = null; // ServiceWorker in the "installed" (waiting) state
let askedToSkip = false;
let reloaded = false;
let work = 0; // in-flight writes / streams / prompts (beginWork / endWork)
let holds = 0; // unsent drafts etc. (hold / release)
let visibleMs = 0; // visible time accumulated while a worker has been waiting
let started = false;
const listeners = new Set();

function emit() {
  listeners.forEach((fn) => {
    try {
      fn(getState());
    } catch {
      /* listener errors must not break the flow */
    }
  });
}

export function getState() {
  return {
    available: Boolean(waiting),
    applying: askedToSkip,
    overdue: Boolean(waiting) && !askedToSkip && visibleMs >= OVERDUE_AFTER_MS,
  };
}

export function subscribe(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

/** Something that must not be interrupted by a reload is running. Always pair with endWork(). */
export function beginWork() {
  work += 1;
}

export function endWork() {
  work = Math.max(0, work - 1);
}

/** The user has unsent / unsaved input on screen. Always pair with release(). */
export function hold() {
  holds += 1;
}

export function release() {
  holds = Math.max(0, holds - 1);
}

function onHoldPath() {
  const path = typeof window !== 'undefined' ? window.location.pathname : '';
  return HOLD_PATHS.some((p) => path.startsWith(p));
}

export function isSafe() {
  return work === 0 && holds === 0 && !onHoldPath();
}

/** A worker is waiting (from onUpdate, or already waiting at page load). */
export function onWaiting(registration) {
  const w = registration && registration.waiting;
  if (!w || w === waiting) return;
  waiting = w;
  askedToSkip = false;
  emit();
  applyIfSafe();
}

/** Ask the waiting worker to take over now (the bar's 更新 button, or a safe moment). */
export function applyNow() {
  if (!waiting || askedToSkip) return;
  askedToSkip = true;
  emit();
  waiting.postMessage({ type: 'SKIP_WAITING' });
}

export function applyIfSafe() {
  if (waiting && !askedToSkip && isSafe()) applyNow();
}

/** Ask the browser to look for a new service-worker.js (one conditional GET, ~2 KB). */
export async function check() {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return;
  try {
    const reg = await navigator.serviceWorker.getRegistration();
    if (!reg) return;
    await reg.update();
    onWaiting(reg);
    applyIfSafe();
  } catch {
    /* offline or SW disabled — try again at the next trigger */
  }
}

/**
 * Wire everything once, after serviceWorkerRegistration.register({ onUpdate: onWaiting }).
 * Safe to call when there is no service worker (dev, unsupported browser): it does nothing.
 */
export function start() {
  if (started || typeof window === 'undefined' || !('serviceWorker' in navigator)) return;
  started = true;

  // A worker that finished installing during a previous visit is already waiting at load
  // and never fires onupdatefound — pick it up here.
  navigator.serviceWorker.ready.then((reg) => onWaiting(reg)).catch(() => {});

  navigator.serviceWorker.addEventListener('controllerchange', () => {
    if (!askedToSkip || reloaded) return;
    reloaded = true;
    window.location.reload();
  });

  const onVisible = () => {
    if (document.visibilityState !== 'visible') return;
    // Coming back is the safe moment: reload before the person starts doing anything.
    applyIfSafe();
    void check();
  };
  document.addEventListener('visibilitychange', onVisible);
  window.setInterval(() => void check(), CHECK_EVERY_MS);

  // Overdue clock for the fallback bar: counts only visible time while a worker waits.
  window.setInterval(() => {
    if (!waiting || askedToSkip || document.visibilityState !== 'visible') return;
    const wasOverdue = getState().overdue;
    visibleMs += TICK_MS;
    if (getState().overdue !== wasOverdue) emit();
  }, TICK_MS);
}

/** Test hook: reset module state. */
export function _resetForTests() {
  waiting = null;
  askedToSkip = false;
  reloaded = false;
  work = 0;
  holds = 0;
  visibleMs = 0;
  started = false;
  listeners.clear();
}
